import { Box, ButtonV2, Divider, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { UseVenueInfoReturn, VenueInfoCompact } from '../VenueInfo/VenueInfo';
import { Bed, Calendar, Guests } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useGuestHotelBookingsListTelemetry } from '../GuestHotelBookingsList/GuestHotelBookingList.telemetry';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useIsAccommodationsTourEnabled } from '@shared/utils/accommodations/useIsAccommodationsTourEnabled';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { routePaths } from '@apps/admin/route.paths';
import { useFormatEventDate } from '@localization/hooks/useFormatEventDate';
import { useEventInfo } from '@shared/utils/eventInfo';
import { GuestsTourStep, StartTourButton } from '../AccommodationsTour';

const MAIN_ACTION_BAR_HEIGHT = { _: pxToRem(34), md: pxToRem(40) };

type AccommodationsListingTab = 'stays' | 'bookings';

type MainActionBarProps = Readonly<{
  eventDate: Maybe<string>;
  isGuestBookingsTrackerPage?: boolean;
  onBadgeClick: () => void;
  pageSlug: string;
  venueInfoProps: UseVenueInfoReturn;
}>;

const EventDate = ({ eventDate }: { eventDate: string }) => {
  const isMobile = useIsMobileScreen();
  return (
    <Flex
      alignItems="center"
      gap={1}
      width="fit-content"
    >
      {!isMobile && <Calendar size={16} />}
      <TextV2
        typographyVariant="label2"
        paddingRight={2}
      >
        {eventDate}
      </TextV2>
    </Flex>
  );
};

const BadgeButton = forwardRef<HTMLButtonElement, { badgeType: AccommodationsListingTab; handleBadgeClick: () => void }>(({ badgeType, handleBadgeClick }, ref) => {
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('travelMap');
  const tabText = badgeType === 'bookings' ? tq`Bookings` : tq`Stays`;
  return (
    <ButtonV2
      ref={ref}
      height={MAIN_ACTION_BAR_HEIGHT}
      backgroundColor="white"
      _hover={{ backgroundColor: 'white' }}
      _active={{ backgroundColor: 'white' }}
      onClick={handleBadgeClick}
      alignItems="center"
      gap={1}
      border="1px solid"
      borderColor="mono3"
      borderRadius="full"
      paddingX={4}
      paddingY={3}
      color="mono14"
      boxShadow="0px 3px 8px 0px rgba(0, 0, 0, 0.03), 0px 10px 60px -20px rgba(44, 41, 37, 0.03), 0px 9px 36px -30px rgba(0, 0, 0, 0.04);"
      position="relative"
    >
      {!isMobile && (
        <>
          {badgeType === 'bookings' && <Guests size={16} />}
          {badgeType === 'stays' && <Bed size={16} />}
        </>
      )}
      <TextV2 typographyVariant="label2">{tabText}</TextV2>
    </ButtonV2>
  );
});

BadgeButton.displayName = 'BadgeButton';

const Badge = ({ badgeType, onBadgeClick, pageSlug }: { badgeType: AccommodationsListingTab; onBadgeClick: () => void; pageSlug: string }) => {
  const telemetry = useGuestHotelBookingsListTelemetry();

  const handleBadgeClick = useCallback(() => {
    if (badgeType === 'stays') {
      telemetry.showEventStaysClicked(pageSlug);
    }

    if (badgeType === 'bookings') {
      telemetry.showEventConfirmedBookingsClicked(pageSlug);
    }

    onBadgeClick();
  }, [badgeType, onBadgeClick, telemetry, pageSlug]);

  return (
    <GuestsTourStep>
      <BadgeButton
        badgeType={badgeType}
        handleBadgeClick={handleBadgeClick}
      />
    </GuestsTourStep>
  );
};

export const MainActionBar = ({ isGuestBookingsTrackerPage, onBadgeClick, pageSlug, venueInfoProps }: MainActionBarProps) => {
  const { tq } = useTranslation('travelMap');
  const router = useRouterHelper();
  const { eventInfo } = useEventInfo();
  const formattedEventDate = useFormatEventDate(eventInfo, 'MMMdd');
  const { isAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();
  const settingsGeneralUrl = useMemo(() => {
    return router.buildPath(routePaths.settingsGeneral.goToPath('eventDate'));
  }, [router]);

  return (
    <Flex
      gap={isAccommodationsTourEnabled ? 3 : 5}
      marginX={6}
      height={MAIN_ACTION_BAR_HEIGHT}
      alignItems="center"
      justifyContent="center"
    >
      <StartTourButton showTour={!isGuestBookingsTrackerPage} />
      <Flex
        border="1px solid"
        borderColor="mono3"
        borderRadius="full"
        alignItems="center"
        paddingY={3}
        boxShadow="0px 3px 8px 0px rgba(0, 0, 0, 0.03), 0px 10px 60px -20px rgba(44, 41, 37, 0.03), 0px 9px 36px -30px rgba(0, 0, 0, 0.04);"
        flex={1}
        backgroundColor="white"
        height={MAIN_ACTION_BAR_HEIGHT}
        overflow="hidden"
      >
        <Box
          flex={1}
          minWidth={0}
        >
          <VenueInfoCompact venueInfoProps={venueInfoProps} />
        </Box>
        {formattedEventDate && (
          <>
            <Divider
              orientation="vertical"
              height="100%"
            />
            <TooltipV2
              content={
                <TextV2
                  maxWidth={pxToRem(256)}
                  typographyVariant="label2"
                >
                  {tq`Joy will look for the best options around your event date. Click here to update the event date.`}
                </TextV2>
              }
            >
              <ButtonV2
                flex={0}
                flexShrink={0}
                flexBasis="auto"
                variant="ghost"
                intent="neutral"
                as="a"
                href={settingsGeneralUrl}
                paddingX={4}
                height={MAIN_ACTION_BAR_HEIGHT}
                alignItems="center"
                justifyContent="center"
                width="fit-content"
              >
                <EventDate eventDate={formattedEventDate} />
              </ButtonV2>
            </TooltipV2>
          </>
        )}
      </Flex>
      <Badge
        badgeType={isGuestBookingsTrackerPage ? 'stays' : 'bookings'}
        onBadgeClick={onBadgeClick}
        pageSlug={pageSlug}
      />
    </Flex>
  );
};
